import React, { useState } from 'react';
import { 
  BarChart, Bar, 
  LineChart, Line, 
  PieChart, Pie, 
  Cell,
  XAxis, YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import { 
  Building2, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Clock,
  Wrench,
  AlertCircle,
  CheckCircle2,
  Calendar,
  BarChart2,
  PieChart as PieChartIcon,
  Activity,
  AlertOctagon,
  Timer,
  Settings,
  Users,
  Zap
} from 'lucide-react';
import { dashboardConfig } from './config';
import StorePerformanceCard from './components/StorePerformanceCard';
import StaffPerformanceCard from './components/StaffPerformanceCard';

const COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#6366F1', // indigo
  '#14B8A6'  // teal
];

const AnalyticsTab = () => {
  const [config, setConfig] = useState(dashboardConfig);
  const [timeRange, setTimeRange] = useState('month');
  const [filterType, setFilterType] = useState('all');
  const [showConfigModal, setShowConfigModal] = useState(false);

  // Add mock data for stores and staff
  const storeData = [
    { name: 'Store #123', maintenanceCost: 12500, ticketCount: 45, responseTime: 2.3 },
    { name: 'Store #456', maintenanceCost: 9800, ticketCount: 38, responseTime: 1.8 },
    { name: 'Store #789', maintenanceCost: 15600, ticketCount: 52, responseTime: 3.1 },
    // Add more stores...
  ];

  const staffData = [
    { name: 'John Smith', closedTickets: 127, avgResponseTime: 1.5 },
    { name: 'Sarah Johnson', closedTickets: 98, avgResponseTime: 2.1 },
    { name: 'Mike Wilson', closedTickets: 145, avgResponseTime: 1.8 },
    // Add more staff...
  ];

  // Add KPI data
  const kpiData = {
    totalSpending: {
      value: 158420,
      trend: -8,
      lastPeriod: 172000,
      icon: DollarSign,
      color: 'blue'
    },
    openTickets: {
      value: 42,
      trend: 5,
      lastPeriod: 40,
      icon: AlertCircle,
      color: 'amber'
    },
    avgResponse: {
      value: 2.5,
      trend: -12,
      lastPeriod: 2.8,
      icon: Clock,
      color: 'green'
    },
    completionRate: {
      value: 94,
      trend: 3,
      lastPeriod: 91,
      icon: CheckCircle2,
      color: 'indigo'
    }
  };

  // Add chart data
  const monthlyTrendsData = [
    { month: 'Jan', spending: 45000, tickets: 85, resolved: 82 },
    { month: 'Feb', spending: 42000, tickets: 75, resolved: 72 },
    { month: 'Mar', spending: 48000, tickets: 90, resolved: 85 },
    { month: 'Apr', spending: 51000, tickets: 95, resolved: 91 },
    { month: 'May', spending: 47000, tickets: 88, resolved: 84 },
    { month: 'Jun', spending: 44000, tickets: 82, resolved: 79 }
  ];

  const issueTypes = [
    { name: 'HVAC', value: 35 },
    { name: 'Plumbing', value: 25 },
    { name: 'Electrical', value: 20 },
    { name: 'Equipment', value: 15 },
    { name: 'Other', value: 5 }
  ];

  // Configuration Modal Component
  const ConfigModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-96">
        <h3 className="text-lg font-semibold mb-4">Dashboard Configuration</h3>
        <div className="space-y-4">
          {Object.entries(config.sections).map(([key, section]) => (
            <div key={key} className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{section.title}</span>
              <button
                onClick={() => toggleSection(key)}
                className={`px-3 py-1 rounded-full text-sm ${
                  section.enabled
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {section.enabled ? 'Enabled' : 'Disabled'}
              </button>
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-end">
          <button
            onClick={() => setShowConfigModal(false)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  const toggleSection = (sectionKey) => {
    setConfig(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...prev.sections[sectionKey],
          enabled: !prev.sections[sectionKey].enabled
        }
      }
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header with Configuration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900">Maintenance Analytics</h2>
            <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm">
              <Activity className="w-4 h-4" />
              <span>Live</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="rounded-lg border border-gray-300 text-sm p-2"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
            <button
              onClick={() => setShowConfigModal(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Settings className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>
      </div>

      {/* Modular Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {config.sections.kpis.enabled && (
          <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                title: 'Total Maintenance Cost',
                value: kpiData.totalSpending.value,
                trend: kpiData.totalSpending.trend,
                icon: DollarSign,
                color: 'blue',
                format: (val) => `$${val.toLocaleString()}`,
                change: '-$13,580',
                period: 'vs. last month'
              },
              {
                title: 'Active Tickets',
                value: kpiData.openTickets.value,
                trend: kpiData.openTickets.trend,
                icon: AlertCircle,
                color: 'rose',
                format: (val) => val,
                change: '+2',
                period: 'vs. yesterday',
                severity: {
                  critical: 5,
                  high: 12
                }
              },
              {
                title: 'Avg Response Time',
                value: kpiData.avgResponse.value,
                trend: kpiData.avgResponse.trend,
                icon: Clock,
                color: 'emerald',
                format: (val) => `${val}h`,
                change: '-30min',
                period: 'vs. target'
              },
              {
                title: 'Resolution Rate',
                value: kpiData.completionRate.value,
                trend: kpiData.completionRate.trend,
                icon: CheckCircle2,
                color: 'indigo',
                format: (val) => `${val}%`,
                change: '+3%',
                period: 'vs. target'
              }
            ].map((stat, index) => {
              const Icon = stat.icon;
              const isPositive = stat.change.startsWith('-');
              const colorMap = {
                blue: 'bg-blue-500 text-blue-50',
                rose: 'bg-rose-500 text-rose-50',
                emerald: 'bg-emerald-500 text-emerald-50',
                indigo: 'bg-indigo-500 text-indigo-50'
              };
              
              return (
                <div 
                  key={stat.title}
                  className="group bg-white rounded-2xl border border-gray-200 hover:border-gray-300 transition-all duration-300"
                >
                  <div className="p-6 space-y-4">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className={`${colorMap[stat.color]} rounded-xl p-2.5`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className={`flex items-center gap-1.5 text-sm font-medium ${
                        isPositive ? 'text-emerald-600' : 'text-rose-600'
                      }`}>
                        {stat.change}
                        <span className="text-gray-500 font-normal">{stat.period}</span>
                      </div>
                    </div>

                    {/* Value and Title */}
                    <div>
                      <div className="flex items-baseline gap-2">
                        <h3 className="text-3xl font-bold text-gray-900">
                          {stat.format(stat.value)}
                        </h3>
                        {stat.severity && (
                          <div className="flex gap-2">
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700">
                              {stat.severity.critical} Critical
                            </span>
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                              {stat.severity.high} High
                            </span>
                          </div>
                        )}
                      </div>
                      <p className="text-base text-gray-600 mt-1">{stat.title}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {config.sections.spendingTrends.enabled && (
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Spending Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={monthlyTrendsData}>
                <defs>
                  <linearGradient id="colorSpending" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="spending" stroke="#3B82F6" fill="url(#colorSpending)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {config.sections.issueDistribution.enabled && (
          <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Issue Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={issueTypes}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                >
                  {issueTypes.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {config.sections.storePerformance.enabled && (
          <div className="lg:col-span-2">
            <StorePerformanceCard data={storeData} />
          </div>
        )}

        {config.sections.staffPerformance.enabled && (
          <div className="lg:col-span-1">
            <StaffPerformanceCard data={staffData} />
          </div>
        )}

        {config.sections.efficiency.enabled && (
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Response Efficiency</h3>
                <p className="text-sm text-gray-500">Average response times by month</p>
              </div>
              <div className="flex items-center gap-2 text-green-600">
                <Timer className="w-5 h-5" />
                <span className="text-sm font-medium">2.5h avg</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyTrendsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="resolved"
                  stroke="#10B981"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {config.sections.priorityMatrix.enabled && (
          <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Priority Matrix</h3>
                <p className="text-sm text-gray-500">Issues by priority level</p>
              </div>
              <AlertOctagon className="w-5 h-5 text-amber-500" />
            </div>
            <div className="space-y-4">
              {[
                { name: 'Critical', value: 15, color: 'red' },
                { name: 'High', value: 30, color: 'amber' },
                { name: 'Medium', value: 40, color: 'blue' },
                { name: 'Low', value: 15, color: 'green' }
              ].map((priority, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">{priority.name}</span>
                    <span className={`text-${priority.color}-600 font-medium`}>
                      {priority.value}%
                    </span>
                  </div>
                  <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-${priority.color}-500`}
                      style={{ width: `${priority.value}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Total Open Issues</span>
                <span className="font-medium text-gray-900">42</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {showConfigModal && <ConfigModal />}
    </div>
  );
};

export default AnalyticsTab; 